import useApiRequest from "@shared/hooks/common/useApiRequest";
import { ErrorResponse } from "@shared/types/commonTypes";
import { Patient, Property } from "@features/clinic-patients/clinic/types";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import { RootState } from "@app/store/store";
import { maskNumber, maskCurrency, maskEmail, maskPhone, maskText } from "@shared/lib";

type UsePatientDetailsResult = {
  patient: Patient | undefined;
  isLoading: boolean;
  isFetching: boolean;
  error: ErrorResponse | null;
};

/**
 * Hook to fetch patient details with optional sensitive data masking.
 * Patient data is cached with previous data as placeholder.
 */
const usePatientDetails = (
  patientId: string,
  clinicId: string
): UsePatientDetailsResult => {
  const { GET } = useApiRequest();
  const shouldMask = useSelector(
    (state: RootState) => state.app.obfuscateSensitiveData
  );
  const sensitiveDataProperties = useSelector(
    (state: RootState) => state.app.sensitiveDataProperties
  );

  const fetchPatientDetails = async (): Promise<Patient> => {
    return GET<Patient>(
      `/api/healthcare-companies/${clinicId}/patients/${patientId}`
    );
  };

  const { data, isLoading, isFetching, error } = useQuery<
    Patient,
    ErrorResponse
  >({
    queryKey: ["patientDetails", patientId],
    queryFn: fetchPatientDetails,
    placeholderData: keepPreviousData,
  });

  const getMaskedValue = (property: Property, value: unknown): string => {
    if (
      !shouldMask ||
      !sensitiveDataProperties.includes(property.name) ||
      value == null
    ) {
      return value?.toString() ?? "";
    }

    if (
      property.name === "payments_total_spent" ||
      property.name === "payments_total_pending_payment"
    ) {
      return maskCurrency(value as string | number);
    } else if (
      property.name === "core_visits_count" ||
      property.name === "core_last_visit_at"
    ) {
      return maskNumber(value as string | number);
    } else if (property.name === "core_email") {
      return maskEmail(value as string);
    } else if (property.name === "core_phone") {
      return maskPhone(value as string);
    } else {
      return maskText(value as string);
    }
  };

  const maskedPatient = data
    ? {
        ...data,
        ...Object.fromEntries(
          Object.entries(data).map(([key, value]) => [
            key,
            sensitiveDataProperties.includes(key)
              ? getMaskedValue({ name: key } as Property, value)
              : value,
          ])
        ),
      }
    : undefined;

  return {
    patient: maskedPatient as Patient,
    isLoading,
    isFetching,
    error: error ?? null,
  } satisfies UsePatientDetailsResult;
};

export default usePatientDetails;
